import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import AdStateContainerRecord from 'PreviewRecords/AdStateContainerRecord';
import DraggableWrapper from 'Components/DraggableWrapper/DraggableWrapper';
import ResizeHandle from 'PreviewComponents/ResizableContainer/ResizeHandle';
import { getMetadata, getAdId } from 'Selectors';
import { getActivePageLayout, getContainerGeometry } from 'PreviewSelectors';
import { updateAdStateContainerDimensions, updateAdStateContainerDragPosition } from 'PreviewActions/customLayoutActions';
import {
  APP,
  PRESENT,
  PREVIEW,
  WIDTH,
  HEIGHT,
} from 'SharedConstants/';

const AdStateContainerDiv = styled.div`
  position: ${props => (props.revealFormat ? 'relative' : 'absolute')};
  background-color: transparent;
  ${props => props.hasSelectedAdStateContainer && { zIndex: '1' }};
  ${props => props.selected && { cursor: 'grabbing' }};
  ${props => props.selected && { pointerEvents: 'all' }};
  ${props => props.selected && { zIndex: '2' }};
`;

const ResizeDefaultHandleContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const ResizeDefaultHandle = styled.div`
  display: flex;
  position: absolute;
  opacity: ${props => (
    props.disabled ? 0 : 0.7
  )};
  pointer-events: ${props => (
    props.disabled ? 'none' : 'auto'
  )}
`;

const ResizeWidthHandle = styled(ResizeDefaultHandle)`
  width: 30px;
  padding-left: 10px;
  align-items: center;
`;

const ResizeHeightHandle = styled(ResizeDefaultHandle)`
  height: 15px;
  padding-top: 10px;
  justify-content: center;
`;

const Row = styled.div`
  display: flex;
  position: relative;
`;

class ActiveAdStateContainer extends PureComponent {
  componentDidMount() {
    const activeContainer = document.querySelector('.active-container');
    activeContainer.style.visibility = 'hidden';
    setTimeout(() => this.updateContainerPosition(), 5);
    const scrollableContainer = document.querySelector('.preview-scrollable-container');
    scrollableContainer.addEventListener('scroll', this.updateContainerPosition);
  }
  componentDidUpdate() {
    const { activePageLayout } = this.props;
    if (activePageLayout.device === 'desktop') {
      setTimeout(() => this.updateContainerPosition(), 5);
    } else {
      this.updateContainerPosition();
    }
  }
  componentWillUnmount() {
    const scrollableContainer = document.querySelector('.preview-scrollable-container');
    scrollableContainer.removeEventListener('scroll', this.updateContainerPosition);
  }
  updateContainerPosition = () => {
    const { adStateContainer } = this.props;
    const activeContainer = document.querySelector('.active-container');
    const adStateContainerGeometry = getContainerGeometry(adStateContainer);
    const offsetElement = document.querySelector('.site-background');
    const domElement = offsetElement.querySelector(`[data-key="${adStateContainer.key}"]`);
    adStateContainerGeometry.left = domElement.getBoundingClientRect().left;
    adStateContainerGeometry.top = domElement.getBoundingClientRect().top;
    adStateContainerGeometry.width = domElement.getBoundingClientRect().width;
    adStateContainerGeometry.height = domElement.getBoundingClientRect().height;
    activeContainer.style.top = `${adStateContainerGeometry.top}px`;
    activeContainer.style.left = `${adStateContainerGeometry.left}px`;
    activeContainer.style.width = `${adStateContainerGeometry.width}px`;
    activeContainer.style.height = `${adStateContainerGeometry.height}px`;
    activeContainer.style.visibility = 'visible';
  };
  resizeAd = (e, ui, dimension) => {
    const { deltaX, deltaY } = ui;
    const { adStateContainer, updateAdStateContainerDimensions } = this.props;
    const adStateContainerGeometry = getContainerGeometry(adStateContainer);
    const isWidth = dimension === WIDTH;
    const inverseDimension = isWidth ? HEIGHT : WIDTH;
    const delta = isWidth ? deltaX : deltaY;
    const newValue = adStateContainerGeometry[dimension] + delta;
    const increasePercentage = delta / adStateContainerGeometry[dimension];
    const newInverseValue = adStateContainerGeometry[inverseDimension] + (adStateContainerGeometry[inverseDimension] * increasePercentage);
    updateAdStateContainerDimensions(adStateContainer.key, dimension, newValue);
    if (e.shiftKey) updateAdStateContainerDimensions(adStateContainer.key, inverseDimension, newInverseValue);
  };
  handleDrag = (e, ui) => {
    e.stopPropagation();
    const { adStateContainer, updateAdStateContainerDragPosition } = this.props;
    const { x, y } = ui;
    const deltaX = x - this.startX;
    const deltaY = (y - this.startY);
    updateAdStateContainerDragPosition(adStateContainer.key, deltaX, deltaY);
  };
  beginDrag = (e, ui) => {
    e.stopPropagation();
    this.startX = ui.lastX;
    this.startY = ui.lastY;
    this.axisLock = false;
  };
  handleMouseUp = (e) => {
    e.stopPropagation();
  };
  render() {
    const { isDevice, resizeAd } = this;
    const { activePageLayout, adStateContainer } = this.props;
    const adStateContainerGeometry = getContainerGeometry(adStateContainer);
    const key = adStateContainer.key;
    const selected = adStateContainer.selected;
    const offsetElement = document.querySelector('.site-background');
    const domElement = offsetElement.querySelector(`[data-key="${adStateContainer.key}"]`);
    adStateContainerGeometry.left = domElement.getBoundingClientRect().left;
    adStateContainerGeometry.top = domElement.getBoundingClientRect().top;
    adStateContainerGeometry.width = domElement.getBoundingClientRect().width;
    adStateContainerGeometry.height = domElement.getBoundingClientRect().height;

    return (
      <Row
        className="active-container"
        style={adStateContainerGeometry}
      >
        <DraggableWrapper
          key={key}
          draggable
          onDrag={this.handleDrag}
          onDragStart={this.beginDrag}
          style={{
            width: `${adStateContainerGeometry.width}px`,
            height: `${adStateContainerGeometry.height}px`,
          }}
        >
          <AdStateContainerDiv
            data-key={key}
            isFixed={adStateContainer.isFixed}
            onClick={this.handleMouseUp}
            style={{
              width: `${adStateContainerGeometry.width}px`,
              height: `${adStateContainerGeometry.height}px`,
            }}
          />
        </DraggableWrapper>
        {selected &&
          <ResizeDefaultHandleContainer>
            <DraggableWrapper
              onDragStart={this.beginDrag}
              onDrag={(e, ui) => resizeAd(e, ui, WIDTH)}
              axis="x"
            >
              <ResizeWidthHandle
                data-key={key}
                style={{
                  right: '-30px',
                  height: `${adStateContainerGeometry.height}px`,
                }}
              >
                <ResizeHandle />
              </ResizeWidthHandle>
            </DraggableWrapper>
            <DraggableWrapper
              onDragStart={this.beginDrag}
              onDrag={(e, ui) => resizeAd(e, ui, HEIGHT)}
              axis="y"
            >
              <ResizeHeightHandle
                data-key={key}
                style={{
                  bottom: '-20px',
                  width: `${adStateContainerGeometry.width}px`,
                }}
              >
                <ResizeHandle axis="y" />
              </ResizeHeightHandle>
            </DraggableWrapper>
          </ResizeDefaultHandleContainer>
        }
      </Row>
    );
  }
}

ActiveAdStateContainer.propTypes = {
  adId: PropTypes.number,
  adStateContainer: PropTypes.instanceOf(AdStateContainerRecord),
  preview: PropTypes.object.isRequired,
  revealFormat: PropTypes.bool,
};

ActiveAdStateContainer.defaultProps = {
  adId: 0,
  revealFormat: false,
};

const mapStateToProps = state => ({
  activePageLayout: getActivePageLayout(state),
  adId: getAdId(state),
  preview: state.getIn([APP, PRESENT, PREVIEW]),
  revealFormat: getMetadata(state).get('revealFormat') || getMetadata(state).get('formatName') === 'Scroller',
});

const mapDispatchToProps = dispatch => ({
  updateAdStateContainerDimensions: (adStateContainerKey, dimension, value) =>
    dispatch(updateAdStateContainerDimensions(adStateContainerKey, dimension, value)),
  updateAdStateContainerDragPosition: (adStateContainerKey, x, y) =>
    dispatch(updateAdStateContainerDragPosition(adStateContainerKey, x, y)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActiveAdStateContainer);



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Preview/components/ActiveAdStateContainer/ActiveAdStateContainer.jsx